import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import {
  IconComponent,
  MessageService,
  ObserveIntersectingDirective,
  ProgressComponent
} from '@factor_ec/ui';
import { Operation } from '@factor_ec/utils';

import { ApiService } from '@/core/api.service';
import { AppService } from '@/core/app.service';
import { SessionService } from '@/core/session.service';
import { RequestData } from '@/core/models/request-data';
import { aliasValidator } from '@/shared/alias.validator';
import { MatSelectModule } from '@angular/material/select';
import { BeneficiaryRepository } from '@/main/repositories/beneficiary-repository';
import { GroupRepository } from '@/main/repositories/group-repository';
import { environment } from '@/environments/environment';
import { IsGrantedDirective } from '@/core/directives/is-granted.directive';
import { ConnectionService } from '@/main/services/connection.service';
import { GroupService } from '@/main/services/group.service';
import { Beneficiary } from '@/main/models/connection';
import { Group } from '@/main/models/group';

/**
 * @description Componente para gestionar los beneficiarios de un crédito
 * @class BeneficiaryComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-beneficiary',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    MatInputModule,
    MatDialogModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSelectModule,
    ObserveIntersectingDirective,
    ProgressComponent,
    IsGrantedDirective,
    IconComponent
  ],
  templateUrl: './beneficiary.component.html',
  styleUrl: './beneficiary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BeneficiaryComponent implements OnInit {
  // Dependency injection
  apiService = inject(ApiService);
  appService: AppService = inject(AppService);
  public readonly beneficiaryRepository = inject(BeneficiaryRepository);
  private readonly connectionService = inject(ConnectionService);
  private readonly groupService = inject(GroupService);
  public readonly groupRepository = inject(GroupRepository);
  sessionService: SessionService = inject(SessionService);
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef);
  formBuilder = inject(FormBuilder);
  messageService = inject(MessageService);

  // Events
  afterSubmit = output<Operation>();

  // Properties
  credit = signal<any | undefined>(undefined);
  form: FormGroup = new FormGroup({});
  public beneficiaries = this.beneficiaryRepository.findBy();
  public groups = this.groupRepository.findBy();
  public submitting = signal<boolean>(false);

  /**
   * @description Constructor del componente
   * @constructor
   */
  constructor() {
    this.initForm();
  }

  /**
   * @description Inicializa el componente
   * @implements OnInit
   */
  ngOnInit(): void {
    this.credit.set(this.data.credit);
    this.loadProperties();
    this.form.patchValue(this.credit());
  }

  compareItem(a: any, b: any): boolean {
    return a && b ? a.sequence === b.sequence : a === b;
  }

  /**
   * @description Inicializa el formulario reactivo
   * @private
   */
  private initForm(): void {
    this.form = this.formBuilder.group({
      operationNumber: null,
      beneficiary: [null, Validators.required],
      group: [null],
      notifyOverduePayment: false,
      notifyPayment: false,
      notifySendingReceipts: false
    });
  }

  private async loadProperties(): Promise<void> {
    const lenderId = this.sessionService.settings()?.lenderId;
    if (lenderId) {
      await Promise.all([
        this.beneficiaries.load({
          lenderId,
          pageNumber: 1,
          pageLimit: environment.pageLimit
        }),
        this.groups.load({
          lenderId,
          pageNumber: 1,
          pageLimit: environment.pageLimit
        })
      ]);
    }
  }

  /**
   * @description Mapea los datos de la solicitud
   * @param {any} data - Datos a mapear
   * @returns {any} Datos mapeados
   */
  mapRequest(data: any): any {
    let request = structuredClone(data);
    const lenderId = this.sessionService.settings()?.lenderId;
    request.lenderId = lenderId;
    request.countryId = this.data.credit.countryId;
    request.productTypeId = 'CON';
    request.beneficiary.alias =
      request.beneficiary.alias || request.beneficiary.description;
    request.beneficiary.notifyOverduePayment = request.notifyOverduePayment;
    request.beneficiary.notifyPayment = request.notifyPayment;
    request.beneficiary.notifySendingReceipts = request.notifySendingReceipts;
    if (!request.group) {
      request.group = { sequence: null };
    }
    delete request.notifyOverduePayment;
    delete request.notifyPayment;
    delete request.notifySendingReceipts;
    return request;
  }

  async openBeneficiary(): Promise<void> {
    const operation = await this.connectionService.openBeneficiary();
    if (operation) {
      this.beneficiaries.reload();
      this.form.get('beneficiary')?.setValue(operation.entity as Beneficiary);
    }
  }
  async openGroup(): Promise<void> {
    const operation = await this.groupService.open();
    if (operation) {
      this.groups.reload();
      this.form.get('group')?.setValue(operation.entity as Group);
    }
  }

  /**
   * @description Envía el formulario para actualizar el beneficiario
   * @returns {Promise<void>}
   */
  async submit(): Promise<void> {
    if (this.form.valid) {
      try {
        const requestBody = this.mapRequest(this.form.value);
        const data: RequestData = {
          microServiceName: 'WSMPCreditManagement',
          resourcePath: `/credit/edit-properties`,
          method: 'PUT',
          useToken: true,
          body: requestBody,
          bodyEncrypted: true,
          responseEncrypted: true
        };
        this.form.disable();
        this.submitting.set(true);
        const response = await this.apiService.call(data);
        this.dialogRef.close();
        this.messageService.show(response.data.message, {
          class: 'ft-message--success',
          icon: 'check--circle'
        });
        this.afterSubmit.emit({
          method: 'update',
          entity: { beneficiary: requestBody.beneficiary, group: requestBody.group }
        });
      } finally {
        this.form.enable();
        this.submitting.set(false);
      }
    }
  }
}
